import { call, put, takeLatest } from "redux-saga/effects";
import { get } from "lodash";

import {
  setFundsLoadRowResult,
  setFundsLoadCompleted,
  setFundsLoadMembers,
  setLoadableBusinesses,
  setLoading,
} from "../actions";
import axios from "../utilities/axiosWrapper";
import { Auth } from "../utilities/auth";

/**
 * Submits the funds load one member at a time.
 *
 * The rows go out sequentially rather than in parallel. Money is moving, and
 * every row writes three ledger rows inside one database transaction; firing a
 * few hundred of those at once buys a little wall-clock time in exchange for
 * lock contention on the same business account and a results table that is
 * hard to read while it fills. Sequential also means the operator can see
 * exactly how far a file got if they close the tab.
 *
 * A failed row is recorded and the run continues. Each row is its own request
 * and its own transaction, so one failure means one member was not loaded, not
 * that the file is in an unknown state.
 */

// The balances service answers with the current envelope: isSuccess, data and
// error{code,message}. The message names the control that refused - a
// terminated member, a prepay company, an expired balance - so it is passed
// through to the screen unchanged rather than replaced with a generic error.
const readResponse = (
  jsonResponse: any
): { data: any; errorMessage: string | null } => {
  const isSuccess = get(jsonResponse, "data.isSuccess");
  if (isSuccess) {
    return { data: get(jsonResponse, "data.data"), errorMessage: null };
  }

  // A rejected axios promise is returned rather than thrown by the wrapper, so
  // the service's own message can sit under either shape.
  const message =
    get(jsonResponse, "data.error.message") ||
    get(jsonResponse, "response.data.error.message") ||
    get(jsonResponse, "message") ||
    "The load was refused";
  return { data: null, errorMessage: message };
};

function* submitFundsLoadWorker(action: any): Generator {
  const businessGuid: string = get(action, "data.businessGuid");
  const rows: any[] = get(action, "data.rows") || [];

  yield put(setLoading({ loading: true, errorMessage: "" }));

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    const rowNumber = index + 1;

    try {
      const apiKeys: any = yield Auth.getAuthTokens();
      const jsonResponse: any = yield call(
        axios,
        {
          method: "post",
          path: `businesses/${businessGuid}/accounts/load`,
          data: {
            personGuid: row.personGuid || "",
            email: row.email || "",
            product: row.product,
            amount: Number(row.amount),
            reason: row.reason,
            // Stable for a given submission and row, so retrying a file that
            // half-failed re-runs only the rows that never landed.
            idempotencyKey: row.idempotencyKey,
          },
        },
        apiKeys
      );

      const { data, errorMessage } = readResponse(jsonResponse);
      if (errorMessage) {
        yield put(
          setFundsLoadRowResult({
            rowNumber,
            status: "failed",
            message: errorMessage,
          })
        );
        continue;
      }

      yield put(
        setFundsLoadRowResult({
          rowNumber,
          // alreadyLoaded means the idempotency key had been used before, so
          // this call moved no money.
          status: data?.alreadyLoaded ? "skipped" : "loaded",
          message: data?.alreadyLoaded
            ? "Already loaded earlier - no money moved"
            : `Loaded ${data?.name || ""}`.trim(),
          transactionGuid: data?.transactionGuid,
          availableBalance: data?.availableBalance,
        })
      );
    } catch (err) {
      yield put(
        setFundsLoadRowResult({
          rowNumber,
          status: "failed",
          message: get(err, "message") || "The load could not be sent",
        })
      );
    }
  }

  yield put(setFundsLoadCompleted({}));
  yield put(setLoading({ loading: false }));
}

export function* submitFundsLoadWatcher(): Generator {
  yield takeLatest("SUBMIT_FUNDS_LOAD", submitFundsLoadWorker);
}

/**
 * Loads the postpay companies for the picker.
 *
 * The super admin company list cannot be used here: it does not return the
 * payment type, so prepay companies could not be filtered out and the operator
 * would be offered companies every load against them would be refused for.
 */
function* fetchLoadableBusinessesWorker(): Generator {
  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(
      axios,
      { method: "get", path: "businesses/loadable" },
      apiKeys
    );

    const { data, errorMessage } = readResponse(jsonResponse);
    if (errorMessage) {
      throw new Error(errorMessage);
    }
    return yield put(setLoadableBusinesses(data || []));
  } catch (err) {
    console.error("failed to load the postpay companies", err);
    return yield put(setLoadableBusinesses([]));
  }
}

/**
 * Loads the members of one company once it is picked.
 *
 * A single company is small enough to hand to the dropdown whole and let it
 * filter locally, which is why there is no per-keystroke request here. The
 * super admin employee list could not have done this anyway: its stored
 * procedure takes only offset, limit and sort, so the search string the list
 * service accepts never reaches it.
 */
function* fetchFundsLoadMembersWorker(action: any): Generator {
  const businessGuid: string = get(action, "data.businessGuid") || "";
  if (!businessGuid) {
    return yield put(setFundsLoadMembers([]));
  }

  try {
    const apiKeys: any = yield Auth.getAuthTokens();
    const jsonResponse: any = yield call(
      axios,
      {
        method: "get",
        path: `businesses/${businessGuid}/accounts/loadable-members`,
      },
      apiKeys
    );

    const { data, errorMessage } = readResponse(jsonResponse);
    if (errorMessage) {
      throw new Error(errorMessage);
    }
    return yield put(setFundsLoadMembers(data || []));
  } catch (err) {
    console.error("failed to load the members of the company", err);
    return yield put(setFundsLoadMembers([]));
  }
}

export function* fetchLoadableBusinessesWatcher(): Generator {
  yield takeLatest("FETCH_LOADABLE_BUSINESSES", fetchLoadableBusinessesWorker);
}

export function* fetchFundsLoadMembersWatcher(): Generator {
  yield takeLatest("FETCH_FUNDS_LOAD_MEMBERS", fetchFundsLoadMembersWorker);
}
